﻿import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo, useState } from "react";
import { useMatchProductsQuery } from "../../api/productApi";
import { useAppDispatch } from "../../app/hooks";
import { type ProductDto } from "../../contracts";
import { getAgentResponseById, type CommercialSimulatorSubBrandAgentResponse } from "../../models/agentResponseModels";
import type { ChatMessageViewModel } from "../../models/chatMessageViewModel";
import { isCommercialSimulatorSubBrandToolRequest, type CommercialSimulatorSubBrandToolRequest, type ProductSelection } from "../../models/toolRequestModels";
import { getToolResponseById, isCommercialSimulatorSubBrandToolResponse, type CommercialSimulatorSubBrandToolResponse } from "../../models/toolResponseModels";
import { setCurrentCommercialPlan } from "../../slices/chatSlice";
import { useChatConversation } from "../context/useChatConversation";
import type { ChatControllerActions } from "../controller/useChatController";
import type { ToolComponentProps } from "./toolRenderer";
import { getUnitOfMeasureAbbreviation } from "../../utils/unitOfMeasureUtils";

const getUnitLabel = (product: ProductDto): string | null => {
    const unitSize = product.unit_size != null
        ? `${product.unit_size}${product.unit != null ? getUnitOfMeasureAbbreviation(product.unit) : ""}`
        : null;

    const unitsPerPack = product.units_per_pack != null
        ? `${product.units_per_pack} pack`
        : null;

    return [unitSize, unitsPerPack].filter(Boolean).join(" / ") || null;
};

const getProductMeta = (product: ProductDto): string => {
    return [
        product.retailer_name,
        product.brand,
        product.sub_brand
    ].filter(Boolean).join(" | ");
};

const getProductIdentifiers = (product: ProductDto): string => {
    return [
        product.sku ? `SKU ${product.sku}` : null,
        product.barcode ? `Barcode ${product.barcode}` : null
    ].filter(Boolean).join(" · ");
};

type ActionButtonsProps = {
    isBusy: boolean;
    isReadOnly: boolean;
    selectedProducts: ProductSelection[];
    message: ChatMessageViewModel;
    actions: ChatControllerActions;
    plan: CommercialSimulatorSubBrandAgentResponse | undefined;
};

const ActionButtons: React.FC<ActionButtonsProps> = ({
    isBusy,
    isReadOnly,
    selectedProducts,
    message,
    actions,
    plan
}) => {
    const dispatch = useAppDispatch();
    return (
        <div className="d-flex align-items-center gap-2 m-2 w-100">
            {!isBusy && !isReadOnly && (
                <>
                    <Button
                        type="button"
                        fillMode="outline"
                        themeColor="error"
                        disabled={isBusy}
                        onClick={actions.cancelToolCallAsync}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={isBusy || selectedProducts.length === 0}
                        className="ms-auto"
                        themeColor="primary"
                        onClick={() => {
                            actions.startSubBrandSimulationAsync({
                                products: selectedProducts
                            }, message);
                        }}
                    >
                        Start Simulation
                    </Button>
                </>
            )}

            {plan != null && (
                <Button
                    type="button"
                    fillMode="outline"
                    themeColor="secondary"
                    size="small"
                    className="ms-auto"
                    onClick={() => {
                        dispatch(setCurrentCommercialPlan(plan));
                    }}
                >
                    Open generated plan
                </Button>
            )}
        </div>
    );
};

const getLastPreviousCommercialSimulatorSubBrandToolResponse = (
    messages: ChatMessageViewModel[] | undefined,
    toolRequestId: number,
    toolCallId: string
): CommercialSimulatorSubBrandToolResponse | undefined => {
    if (!messages?.length) {
        return undefined;
    }

    return [...messages]
        .filter(message => message.id !== toolRequestId)
        .sort((a, b) => (b.sequence ?? 0) - (a.sequence ?? 0))
        .find((message): message is ChatMessageViewModel & { payload: CommercialSimulatorSubBrandToolResponse } =>
            isCommercialSimulatorSubBrandToolResponse(message.payload)
            && message.payload.tool_call.id !== toolCallId
        )?.payload;
};

const SubBrandToolCallSteps: React.FC<ToolComponentProps> = ({
    message
}: ToolComponentProps) => {
    const {
        chatMessages,
        isChatBusy,
        actions
    } = useChatConversation();

    if (message?.id == null || !isCommercialSimulatorSubBrandToolRequest(message.payload)) {
        throw new Error("Unknown message tool request type or state");
    }

    const toolRequest: CommercialSimulatorSubBrandToolRequest = message.payload;

    const selectedOptions = getToolResponseById<CommercialSimulatorSubBrandToolResponse>(chatMessages, toolRequest.id)?.tool_call_user_selection;
    const planResponse = getAgentResponseById<CommercialSimulatorSubBrandAgentResponse>(chatMessages, toolRequest.id);

    const isReadOnly = planResponse != null || selectedOptions != null;
    const { data, isFetching, isError } = useMatchProductsQuery(toolRequest && toolRequest.input
        ? {
                search_term: toolRequest.input?.sub_brand_name?.base_name ?? "",
                retailer_name: toolRequest.input?.retailers?.length ? toolRequest.input.retailers[0].retailer_name : "",
                quantity1: toolRequest.input?.sub_brand_name?.quantity_1,
                quantity2: toolRequest.input?.sub_brand_name?.quantity_2,
                limit: 999
            }
        : skipToken);

    const previousResponse = getLastPreviousCommercialSimulatorSubBrandToolResponse(chatMessages, message.id, toolRequest.id);
    const [selectedProducts, setSelectedProducts] = useState<ProductSelection[]>(selectedOptions?.products
        ?? previousResponse?.tool_call_user_selection.products
        ?? []);

    const productIds = selectedProducts.map(o => o.id);
    const products = data?.items ?? [];
    const selectedProductIdSet = useMemo(() => new Set(productIds), [productIds]);

    const handleProductToggle = (product: ProductDto, isSelected: boolean): void => {
        setSelectedProducts(current => isSelected
            ? [
                    ...current,
                    {
                        id: product.id,
                        retailer_id: product.retailer_id,
                        retailer_name: product.retailer_name,
                        name: product.name
                    }
                ]
            : current.filter(selectedProduct => selectedProduct.id !== product.id)
        );
    };

    return (
        <div className="border rounded-3 bg-white shadow-sm">
            <div className="px-2 py-2">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                    <p className="small fw-semibold mb-0 lh-sm">
                        Matched products
                    </p>

                    <div className="d-flex align-items-center gap-2">
                        <span className="badge rounded-pill text-bg-light border small">
                            {products.length}
                            {" "}
                            {products.length === 1 ? "product" : "products"}
                        </span>

                        <span className="badge rounded-pill text-bg-secondary small">
                            {selectedProducts.length}
                            {" selected"}
                        </span>
                    </div>
                </div>

                {isFetching && (
                    <p className="small text-muted mb-0">
                        Loading matching products...
                    </p>
                )}

                {isError && (
                    <div className="alert alert-danger py-1 px-2 small mb-0" role="alert">
                        Unable to load matching products.
                    </div>
                )}

                {!isFetching && !isError && products.length === 0 && (
                    <div className="alert alert-warning py-1 px-2 small mb-0" role="alert">
                        No matching products were found.
                    </div>
                )}

                {!isFetching && !isError && products.length > 0 && (
                    <div className="list-group list-group-flush border rounded-2 overflow-y-auto overflow-x-hidden" style={{ maxHeight: "360px" }}>
                        {products.map(product => {
                            const unitLabel = getUnitLabel(product);
                            const productMeta = getProductMeta(product);
                            const identifiers = getProductIdentifiers(product);

                            return (
                                <label
                                    className="list-group-item list-group-item-action d-flex align-items-start gap-2 py-1 px-2"
                                    key={product.id}
                                >
                                    <div className="pt-1">
                                        <Checkbox
                                            size="large"
                                            disabled={isReadOnly}
                                            checked={selectedProductIdSet.has(product.id)}
                                            onChange={event => handleProductToggle(product, event.value)}
                                        />
                                    </div>

                                    <div className="min-w-0 flex-grow-1 small lh-sm">
                                        <div className="fw-semibold text-wrap">
                                            {product.name}
                                        </div>

                                        {productMeta && (
                                            <div className="text-body-secondary text-wrap">
                                                {productMeta}
                                            </div>
                                        )}

                                        {identifiers && (
                                            <div className="text-muted text-wrap">
                                                {identifiers}
                                            </div>
                                        )}
                                        {unitLabel && (
                                            <div className="mt-1">
                                                <span className="badge text-bg-light border text-wrap text-start">
                                                    {unitLabel}
                                                </span>
                                            </div>
                                        )}

                                    </div>
                                </label>
                            );
                        })}

                    </div>
                )}
                <div className="d-flex w-100">
                    <ActionButtons
                        isBusy={isChatBusy}
                        isReadOnly={isReadOnly}
                        actions={actions}
                        message={message}
                        selectedProducts={selectedProducts}
                        plan={planResponse}
                    />
                </div>
            </div>
        </div>
    );
};

export default SubBrandToolCallSteps;
